import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { ArbetsorderIdFromBeteckningResponseDTO } from "~app/common/models/planera/ArbetsorderIdFromBeteckningResponseDTO";
import { ArbetsorderNameInfoDTO } from "~app/common/models/planera/v2/ArbetsorderNameInfoDTO";
import { VolymAttFordelaDTO } from "~app/common/models/planera/v2/VolymAttFordelaDTO";
import { TullkontorDTO } from "~app/common/models/tullkontor/TullkontorDto";
import { ApiClientService } from "~app/common/services/api-client.service";
import { EventService, IEventTransferObject } from "~app/common/services/cqs/event.service";
import { ICommand } from "~app/common/services/cqs/model-action.service";
import { IArbetsorderDetailResponse } from "~app/features/planera/model/IArbetsorderDetailResponse";
import { IForeslagetProduktionsdatum } from "~app/features/planera/model/IForeslagetProduktionsdatum";
import { IGetArbetsorderRequest } from "~app/features/planera/model/IGetArbetsorderRequest";
import { IGetArbetsorderResponse } from "~app/features/planera/model/IGetArbetsorderResponse";
import { IPlaneraBasdataResponse } from "~app/features/planera/model/IPlaneraBasdataResponse";
import { IReturnToGissResponse } from "~app/features/planera/model/IReturnToGissResponse";
import { AvlaggVagInfoDto } from "../arbetsorder-drivningspunkter/avlagg-vaginfo/avlaggVaginfoModels";
import { IUppdrag } from "../model/IUppdrag";
import { ISaveArbetsorderRequest, ISaveArbetsorderResponse } from "../planera-detail/planera-detail-models";

@Injectable({
    providedIn: "root"
})
export class PlaneraService {
    private readonly apiClientService = inject(ApiClientService);
    private readonly eventService = inject(EventService);

    getForeslagetProduktionsdatum(arbetsorderId: number): Observable<IForeslagetProduktionsdatum> {
        return this.apiClientService.get<IForeslagetProduktionsdatum>(`arbetsorder/${arbetsorderId}/HamtaForeslagetProduktionsdatum`);
    }

    getPlaneraBasdata(): Observable<IPlaneraBasdataResponse> {
        return this.apiClientService.get<IPlaneraBasdataResponse>(`Planera/GetPlaneraBasdata`, true);
    }

    searchForArbetsorder(request: IGetArbetsorderRequest): Observable<IGetArbetsorderResponse> {
        return this.apiClientService.post(`Planera/SearchForArbetsorder`, request, true);
    }

    getArbetsorderDetail(id: number): Observable<IArbetsorderDetailResponse> {
        return this.apiClientService.get(`Planera/GetArbetsorderDetail/${id}`, true);
    }

    getArbetsorderNameInfo(id: number): Observable<ArbetsorderNameInfoDTO> {
        return this.apiClientService.get(`Planera/GetArbetsorderNameInfo`, false, { arbetsorderId: id });
    }

    getVolymAttFordela(id: number): Observable<VolymAttFordelaDTO[]> {
        return this.apiClientService.get(`Planera/GetVolymAttFordela`, false, { arbetsorderId: id });
    }

    getTullkontor(): Observable<TullkontorDTO[]> {
        return this.apiClientService.get(`tullkontor`);
    }

    getUppdrag(): Observable<IUppdrag[]> {
        return this.apiClientService.get<IUppdrag[]>(`Planera/GetUppdrag`, true);
    }

    handleCommand(command: ICommand): Observable<boolean> {
        return this.apiClientService.post(`cqs/command`, command);
    }

    hamtaDrivningspunkterFranKontrakt(id: number, rowVersion: string) {
        return this.apiClientService.post(`Arbetsorder/${id}/HamtaDrivningspunkterFranVirkeskopskontrakt`,
            {
                arbetsorderId: id,
                rowVersion: rowVersion
            }, false);
    }

    performUtbytessimulering(arbetsorderId: number, rowVersion: string): Observable<IArbetsorderDetailResponse> {
        return this.apiClientService.put(`Planera/PerformUtbytessimulering`,
            {
                id: arbetsorderId,
                rowVersion: rowVersion
            }, true);
    }

    returnToGiss(arbetsorderId: number, atgardGuid: string, rowVersion: string, comment: string): Observable<IReturnToGissResponse> {
        return this.apiClientService.put(`Arbetsorder/${arbetsorderId}/ReturnToGiss`,
            {
                arbetsorderId: arbetsorderId,
                atgardGuid: atgardGuid,
                rowVersion: rowVersion,
                comment: comment
            }, false);
    }

    saveArbetsorder(request: ISaveArbetsorderRequest): Observable<any> {
        return this.apiClientService.post<ISaveArbetsorderRequest, ISaveArbetsorderResponse>(`Planera/SaveArbetsorder`, request, true)
            .pipe(tap((arbetsorder) => this.emitIfEvents(arbetsorder)));
    }

    doesArbetsorderExist(arbetsorderId: number): Observable<boolean> {
        return this.apiClientService.get(`Planera/DoesArbetsorderExist`, false, {
            arbetsorderId: arbetsorderId
        });
    }

    getArbetsorderIdFromBeteckning(beteckning: string): Observable<ArbetsorderIdFromBeteckningResponseDTO> {
        return this.apiClientService.get<ArbetsorderIdFromBeteckningResponseDTO>("Planera/GetArbetsorderIdFromBeteckning", false, {
            beteckning: beteckning
        });
    }

    getAvlaggVagInfo(arbetsorderId: number): Observable<AvlaggVagInfoDto[]> {
        return this.apiClientService.get<AvlaggVagInfoDto[]>("Planera/GetAvlaggVagInfo", false, {
            arbetsorderId: arbetsorderId
        });
    }

    private emitIfEvents<T extends IEventTransferObject>(data: T) {
        if (data && data.events) {
            data.events.forEach(this.eventService.emit);
        }
        return data;
    }
}
